import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { MosaicLogoFull } from '@/components/MosaicLogo';
import { createJingleContext, playJingle } from '@/utils/loginJingle';
import { GraduationCap, Building2, Users, Mail, Lock, AlertCircle } from 'lucide-react';

const Login = () => {
  const { loginWithGoogle, loginWithEmail, loginWithMicrosoft, loginWithClever } = useAuth();
  const navigate = useNavigate();
  const [showSSO, setShowSSO] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Please enter both email and password');
      return;
    }
    // Create AudioContext NOW — synchronously during user click gesture
    const jingleCtx = createJingleContext();
    setLoading(true);
    try {
      const result = await loginWithEmail(email.trim(), password);
      // Play jingle with the pre-created context, then navigate
      playJingle(jingleCtx);
      const dest = result?.role === 'super_admin' ? '/superadmin'
        : result?.role === 'erma_reviewer' ? '/erma-review'
        : result?.role === 'sales_rep_universal' ? '/demo-launcher'
        : result?.role === 'parent' ? '/parent'
        : result?.role === 'district_admin' ? '/admin/district'
        : result?.role === 'school_admin' ? '/admin/school'
        : result?.role === 'teacher' ? '/teacher'
        : result?.role === 'student' ? '/my-assessment'
        : '/';
      // Small delay so audio starts before navigation re-render
      setTimeout(() => navigate(dest), 120);
    } catch (err) {
      setError(err.message);
      if (jingleCtx) jingleCtx.close().catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <MosaicLogoFull />
          </Link>
          <p className="text-xs tracking-widest uppercase text-gray-600 font-medium mt-2" style={{ letterSpacing: '0.12em' }}>
            Multilingual Oral &amp; Written Student Assessment for Instructional Clarity
          </p>
        </div>

        {/* Login Card */}
        <div className="border-2 border-black bg-white p-8">
          <h1 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sign In
          </h1>
          <p className="text-gray-600 text-center mb-8">
            Choose your sign-in method
          </p>

          <div className="space-y-4">
            {/* Email/Password — PRIMARY default (most users) */}
            <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
              {error && (
                <div className="bg-red-50 border-2 border-red-200 p-3 flex items-center gap-2 text-sm text-red-700" data-testid="login-error">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  {error}
                </div>
              )}
              <div>
                <Label htmlFor="loginEmail" className="text-sm font-semibold mb-1 block">Email</Label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="loginEmail"
                    type="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    className="h-12 pl-10 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="loginPassword" className="text-sm font-semibold mb-1 block">Password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="loginPassword"
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="h-12 pl-10 border-2 border-gray-300 focus:border-[#002FA7] rounded-none"
                    data-testid="password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-sharp-primary h-12"
                data-testid="email-login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-4 text-sm text-gray-600">or continue with</span>
              </div>
            </div>

            {/* SSO — collapsed by default, user opts in */}
            {!showSSO ? (
              <Button
                onClick={() => setShowSSO(true)}
                variant="outline"
                className="w-full btn-sharp h-12 flex items-center justify-center gap-3 text-sm"
                data-testid="show-sso-btn"
              >
                Google Workspace · Microsoft 365 · Clever
              </Button>
            ) : (
              <div className="space-y-3" data-testid="sso-options">
                {/* Google Workspace */}
                <Button
                  onClick={loginWithGoogle}
                  className="w-full btn-sharp h-14 flex items-center justify-center gap-3"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Continue with Google Workspace</span>
                </Button>

                {/* Microsoft Office 365 */}
                <Button
                  onClick={loginWithMicrosoft}
                  variant="outline"
                  className="w-full btn-sharp h-14 flex items-center justify-center gap-3"
                  data-testid="microsoft-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 23 23">
                    <path fill="#f25022" d="M1 1h10v10H1z"/>
                    <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                    <path fill="#7fba00" d="M12 1h10v10H12z"/>
                    <path fill="#ffb900" d="M12 12h10v10H12z"/>
                  </svg>
                  <span>Continue with Microsoft Office 365</span>
                </Button>

                {/* Clever */}
                <Button
                  onClick={loginWithClever}
                  variant="outline"
                  className="w-full btn-sharp h-14 flex items-center justify-center gap-3"
                  data-testid="clever-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 32 32" fill="none">
                    <rect width="32" height="32" rx="6" fill="#4274F6"/>
                    <path d="M16 6C10.477 6 6 10.477 6 16s4.477 10 10 10 10-4.477 10-10S21.523 6 16 6zm0 17c-3.866 0-7-3.134-7-7s3.134-7 7-7 7 3.134 7 7-3.134 7-7 7z" fill="white"/>
                  </svg>
                  <span>Continue with Clever</span>
                </Button>
              </div>
            )}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center mb-4">
              Or continue without signing in
            </p>
            <Link to="/grades">
              <Button variant="outline" className="w-full btn-sharp h-12">
                <GraduationCap className="mr-2" size={18} />
                Take Practice Exam (Guest)
              </Button>
            </Link>
          </div>
        </div>

        {/* Role Info */}
        <div className="mt-6 grid grid-cols-3 gap-4 text-center text-sm">
          <div className="p-4 border border-gray-200 bg-white">
            <Users className="mx-auto mb-2 text-gray-400" size={20} />
            <div className="font-semibold">Students</div>
            <div className="text-gray-500 text-xs">Take exams</div>
          </div>
          <div className="p-4 border border-gray-200 bg-white">
            <GraduationCap className="mx-auto mb-2 text-gray-400" size={20} />
            <div className="font-semibold">Teachers</div>
            <div className="text-gray-500 text-xs">Grade & Track</div>
          </div>
          <div className="p-4 border border-gray-200 bg-white">
            <Building2 className="mx-auto mb-2 text-gray-400" size={20} />
            <div className="font-semibold">Admins</div>
            <div className="text-gray-500 text-xs">Manage Schools</div>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-600">
          By signing in, you agree to the NYC DOE acceptable use policy.
        </p>
        <p className="mt-2 text-center text-xs text-gray-600">
          &copy; {new Date().getFullYear()} MOSAIC &bull; Created by <a href="https://mosaicassessmentco.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700">Lerno Co.</a>
        </p>
      </div>
    </div>
  );
};

export default Login;
